import classNames from "classnames";
import useUpdateEffect from "hooks/useUpdateEffect";
import { compact, find, flatten, map, noop, reduce, uniqBy } from "lodash";
import React, { useLayoutEffect, useRef, useState } from "react";

import ACDropdown, { ACDropdownOptionProps } from "ac-components/ACDropdown";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";

import "./ACTabs.less";

export type ACTabItem = {
  label: React.ReactNode;
  labelString?: string;
  value: React.Key;
  hidden?: boolean;
  children?: ACTabItem[];
  disabled?: boolean;
  testId?: string;
  optional?: boolean;
};

type Props = {
  items: ACTabItem[];
  responsive?: true;
  value?: React.Key;
  onChange?: (value: React.Key) => void;
  size?: "sm" | "md";
  allowSearch?: boolean;
};

const ACTabs = React.memo(function ACTabs({
  items,
  responsive,
  value,
  onChange = noop,
  size = "md",
  allowSearch,
}: Props) {
  const itemsRef = useRef<{ [key: React.Key]: HTMLDivElement }>({});
  const itemsWidthRef = useRef<{ [key: React.Key]: number }>({});
  const listElementRef = useRef<HTMLDivElement | null>();
  const wrapperRef = useRef<HTMLDivElement | null>();

  const [hiddenTabs, setHiddenTabs] = useState<ACTabItem[]>(() =>
    items.filter((item) => item.hidden),
  );
  const hiddenTabValues = hiddenTabs.map((i) => i.value);

  useUpdateEffect(() => {
    setHiddenTabs(items.filter((item) => item.hidden));
  }, [items]);

  useLayoutEffect(() => {
    if (!responsive) {
      return;
    }
    const handleResize = () => {
      if (!listElementRef.current || !wrapperRef.current) {
        return;
      }
      const threshold = 30;
      const listElement = listElementRef.current;
      const hiddenTabValues = hiddenTabs.map((i) => i.value);
      const wrapperBound = wrapperRef.current.getBoundingClientRect();
      const listElementBound = listElementRef.current.getBoundingClientRect();
      if (wrapperBound.width > listElementBound.width) {
        const hiddenTabsDueToSpace = hiddenTabs.filter((i) => !i.hidden);
        const tabsToShow: React.Key[] = [];
        if (hiddenTabsDueToSpace.length) {
          for (let i = 0; i < hiddenTabsDueToSpace.length; i++) {
            const item = hiddenTabsDueToSpace[i];
            // width of list container + width of all items that will be shown + gaps between them
            const listElementWidth =
              listElementBound.width +
              reduce(
                tabsToShow,
                (w, value) => w + itemsWidthRef.current[value],
                0,
              ) +
              ((tabsToShow.length || 1) - 1) * 8;
            if (
              itemsWidthRef.current[item.value] &&
              wrapperBound.width >
                listElementWidth + itemsWidthRef.current[item.value] + threshold
            ) {
              tabsToShow.push(item.value);
            } else {
              break;
            }
          }
        }
        if (tabsToShow.length) {
          setHiddenTabs((prev) => {
            const _tabs = prev.filter((i) => !tabsToShow.includes(i.value));
            return _tabs;
          });
        }
        return;
      }
      const visibleItems = items.filter(
        (item) => !hiddenTabValues.includes(item.value),
      );
      const moreOptionWidth =
        listElement
          .querySelector(`[data-value="hiddenItems"]`)
          ?.getBoundingClientRect().width ?? 0;
      const _items: ACTabItem[] = [];
      let counter = visibleItems.length - 1;
      visibleItems
        .slice()
        .reverse()
        .forEach((item) => {
          const child = listElement.querySelector(
            `[data-value="${item.value}"]`,
          );
          if (child) {
            const bounds = child.getBoundingClientRect();
            if (
              wrapperBound.left + wrapperBound.width - moreOptionWidth <
              bounds.left + bounds.width + threshold
            ) {
              _items.push(visibleItems[counter]);
            }
          }
          counter = counter - 1;
        });
      if (_items.length) {
        // Items hidden due to space issue should be listed at the top with a divider
        // _items contain reverse list as we are checking space from right side
        setHiddenTabs((prev) =>
          uniqBy([..._items.reverse(), ...prev], "value"),
        );
      }
    };

    // requestAnimationFrame - to avoid circular effect
    requestAnimationFrame(handleResize);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [responsive, hiddenTabs, items, value]);

  const computeTestId = (item?: ACTabItem) => {
    return (
      item?.testId ||
      (typeof item?.label === "string"
        ? `tab-${item.label.toLowerCase().replace(/\s+/g, "-")}`
        : `tab-${String(item?.value ?? "unknown").toLowerCase()}`)
    );
  };

  const renderItem = (item: ACTabItem, index: number) => {
    const isActive = item.value === value;
    const allChildren = [
      ...(item.children || []),
      ...compact(flatten(map(item.children, (c) => c.children))),
    ];
    const isChildActive =
      !isActive && allChildren.length && find(allChildren, { value });

    const tabItemJsx = (
      <div
        data-value={item.value}
        data-test-id={computeTestId(item)}
        className={classNames(`ac-tab-item ac-tab-${size}`, {
          "ac-tab-active": isActive || isChildActive,
          "ac-tab-disabled": item.disabled,
        })}
        key={item.value}
        ref={(d) => {
          if (d) {
            itemsRef.current[item.value] = d;
            itemsWidthRef.current[item.value] = d.getBoundingClientRect().width;
          }
        }}
        onClick={
          item.children || item.disabled
            ? undefined
            : () => {
                if (isActive) return;
                onChange(item.value);
              }
        }
      >
        <span
          className={classNames("ac-tab-item-label", {
            "is-dropdown": item.children?.length,
          })}
        >
          {item.label}
        </span>
        {isChildActive ? (
          <>: &nbsp;{find(allChildren, { value })?.label}</>
        ) : undefined}{" "}
        {item.children ? <ChevronDownIcon /> : null}
      </div>
    );
    if (item.children?.length) {
      const items: ACDropdownOptionProps[] = item.children.map((i) => ({
        label: i.label,
        value: i.value,
        disabled: i.disabled,
        labelString: i.labelString,
        items: i.children?.map((c) => ({
          label: c.label,
          value: c.value,
          disabled: c.disabled,
          labelString: c.labelString,
        })),
      }));
      const lastIndex = item.children.findIndex((item) => item.hidden === true);
      if (lastIndex > 0) {
        items.splice(lastIndex, 0, { type: "divider" });
      }
      return (
        <ACDropdown
          placement={index < 6 ? "bottomLeft" : "bottomRight"}
          items={items}
          onOptionClick={(clickedValue) => {
            if (clickedValue === value) return;
            onChange(clickedValue);
          }}
          getPopupContainer={() => itemsRef.current[item.value]}
          allowSearch={allowSearch}
        >
          {tabItemJsx}
        </ACDropdown>
      );
    }
    return tabItemJsx;
  };

  const visibleItems = items.filter(
    (item) => !hiddenTabValues.includes(item.value),
  );
  return (
    <div className="ac-tabs-wrapper" ref={(d) => (wrapperRef.current = d)}>
      <div className="ac-tabs-list" ref={(r) => (listElementRef.current = r)}>
        {visibleItems.map((item, index) => (
          <React.Fragment key={item.value || index}>
            {renderItem(item, index)}
          </React.Fragment>
        ))}
        {hiddenTabs.length ? (
          <React.Fragment key="hiddenItems">
            {renderItem(
              {
                label: "More",
                value: "hiddenItems",
                children: hiddenTabs,
              },
              visibleItems.length,
            )}
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
});

export default ACTabs;
